import { get as serverGet } from '../server';
import { urls, CATEGORIES, CATEGORY_REQUIRED_HEADERS } from '../reducers/categories';
import { urls as postingUrls } from '../reducers/posting';
import * as urlHelper from 'Helpers/url';
import { getMappedCategories, mapSubcategories } from '../reducers/componentHelpers';
import { paramsToQueryString } from '../../helpers/url';
import { createSelector } from 'reselect';
import { getType } from 'Reducers/helpers/typeModifier';

const _isError = (state, type) => state.categories.isError[type];
const isFetching = (state, type) => state.categories.isFetching[type];

/**
 * Makes an API request for the provided categoryID
 * @param {*} categoryID
 * @param {*} fetchOnError
 */
export const getCategoryById = (categoryID, fetchOnError = false) => (dispatch, getState, { get }) => {
    const state = getState();
    const { categories: { isFetching: isFetchingUrl, elements, isError }} = state;
    const params = { full: true };

    if (state.api && state.api.queryParam && state.api.queryParam.enableMultiLang) {
        params.lang = state.api.queryParam.lang;
    }
    const url = `${urls.getCategory(categoryID)}?${urlHelper.paramsToQueryString(params)}`;
    const type = getType('ELEMENT_CATEGORIES', `${url}&full=true`);

    const hasError = isError[type];

    const shouldFetch = categoryID && (fetchOnError || !hasError) && !(isFetchingUrl[type] || (elements[categoryID] && elements[categoryID].params));

    if (shouldFetch) {
        return dispatch(get(urls.getCategory(categoryID), 'ELEMENT_CATEGORIES', { full: true }, CATEGORY_REQUIRED_HEADERS));
    }
    return Promise.resolve();
};

export const isCategoriesFetching = state => isFetching(state, getType(CATEGORIES, urls.getCategories));
export const isCategoriesError = state => _isError(state, getType(CATEGORIES, urls.getCategories));
export const getCategories = () => (dispatch, getState) => {
    const state = getState();

    if (
        state.categories
        && (
            isCategoriesFetching(state)
            || state.categories.collections[getType(CATEGORIES, urls.getCategories)]
        )
    ) {
        return Promise.resolve();
    }

    return dispatch(serverGet(urls.getCategories, CATEGORIES, {}, CATEGORY_REQUIRED_HEADERS));
};

export const getFullCategories = (baseUrl = '', headers = {}) => (dispatch, getState) => {
    const state = getState();
    const url = `${urls.getCategories}?full=true`;
    const type = getType(CATEGORIES, url);

    if (
        state.categories
            && (
                isFetching(state, type)
                    || state.categories.collections[type]
            )
    ) {
        return Promise.resolve();
    }

    return dispatch(serverGet(`${baseUrl}${url}`, CATEGORIES, {}, headers));
};

const getCategoryUrl = ({ category_id, ...params }) => {
    const strParams = paramsToQueryString(params);
    const url = urls.getCategory(category_id);

    return strParams ? `${url}?${strParams}` : url;
};

export const isGetCategoryFetching = (state, params = {}) => isFetching(state, getType('ELEMENT_CATEGORIES', getCategoryUrl(params)));

/**
 * Makes an API request to pull image for the provided categoryID
 * @param {*} categoryID
 * @param {*} fetchOnError
 */

export const getPopularBrandImagesById = (categoryID, fetchOnError = false) => (dispatch, getState, { get }) => {
    const state = getState();
    const { categories: { isFetching: isFetchingUrl, elements, isError }} = state;
    const params = { id: `popular_make_olxin_${categoryID}`, category_id: categoryID, version: '1.0.0', platform: 'web-desktop', noCache: true, full: true };
    const url = `${postingUrls.getPopularBrandsImage}?${urlHelper.paramsToQueryString(params)}`;
    const type = getType('ELEMENT_CATEGORY_POPULAR_MAKE_IMG', url);
    const hasError = isError[type];
    const isFetchingData = isFetchingUrl[type];
    const hasParams = elements[categoryID]?.params;

    const shouldFetch = categoryID && (fetchOnError || !hasError) && !isFetchingData && !hasParams;

    if (shouldFetch) {
        return dispatch(get(postingUrls.getPopularBrandsImage, 'ELEMENT_CATEGORY_POPULAR_MAKE_IMG', params, CATEGORY_REQUIRED_HEADERS));
    }
    return Promise.resolve();
};

/**
 * @param {Object} state - Redux State
 * @return {Array} - Sorted categories (First level are roots): [
 *   {
 *     id: Category Id
 *     name: Category Name (capitalized, it can be used to display in UI)
 *     key:
 *     sub_categories: Array of child categories (optional)
 *     parent_id: Id of parent category, `0` if root
 *   }, ...
 * ]
 */
export const mapStateToCategories = createSelector(
    state => state.categories,
    categories => getMappedCategories(CATEGORIES, categories, urls.getCategories)
);

/**
 * @param {Object} state - Redux State
 * @param {String} categoryId - Category Id
 * @return {Object} - {
 *     id: Category Id
 *     name: Category Name (capitalized, it can be used to display in UI)
 *     key:
 *     sub_categories: Array of child categories (optional)
 *     parent_id: Id of parent category, `0` if root
 * }
 */
export const mapStateToCategory = (state, categoryId) => {
    const category = state.categories.elements[categoryId];

    if (!category) {
        return {};
    }

    return mapSubcategories(state.categories, category);
};
